import { Drawer } from "antd";
import { useEffect, useState } from "react";
import { formatSharedDate } from "../../utils/dateFormatting";
import {
  DrawerContent,
  DrawerHeaderLabel,
  DrawerlabelContainer,
  DrawerWrapper,
} from "./DrawerStyled";
import FilePropertiesTab from "./FilePropertiesTab";
import PeopleWithAccessTab from "./PeopleWithAccessTab";
import { BackButton } from "../../pages/MatterDetails/MatterDetailsStyled";
import { X } from "@phosphor-icons/react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  fetchMatterDetail,
  fetchMatterDetailList,
  getDocumentDetails,
  getShareRecipients,
  optimisticCategoryMove,
  reconcileCategoryMove,
  restoreCategoryMoveSnapshot,
  setUpdateMeta,
  updateCategory,
} from "../../slice/MatterDetailsSlice";
import PropTypes from "prop-types";
import { StyledTabs } from "./StyledTabs";
import { createGlobalStyle } from 'styled-components';
const GlobalDrawerStyles = createGlobalStyle`
  .custom-drawer-lower-component .ant-drawer-content-wrapper {
    top: 12vh !important;
  }
`;
const DrawerComponent = ({
  open,
  setOpen,
  fileProp,
  currentPage,
  sort,
  sortBy,
  setError,
  setShowNotification,
  setShowShareModal,
  reviewers,
  setReviewers,
  handleRemindClick,
  remindStatus,
  setRemindStatus,
  remindInProgressIds,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [dropdownDisabled, setDropdownDisabled] = useState(false);
  const {
    categoryList,
    documentDetail,
    matterDetailsList,
    matterDetailsTree,
    totalRecords,
    updateMeta,
  } = useSelector(
    (state) => state.matterDetails
  );
  const { currentFolderId } = useSelector(
    (state) => state.websocket
  )

  const onClose = () => {
    setOpen(false);
  };

  const handleCaseTypeSelect = async (selectedValue) => {
    setDropdownDisabled(true);

    try {
      await handleSubmit(selectedValue);
    } catch (err) {
      console.error("Failed to update category:", err);
    } finally {
      setDropdownDisabled(false);
    }
  };

  const handleSubmit = (selectedValue) => {
    const docId = fileProp.unique_id.substring(1);
    const matterId = matterDetailsTree?.id || id;
    const snapshot = JSON.parse(
      JSON.stringify({
        matterDetailsTree,
        matterDetailsList,
        updateMeta,
        documentDetail,
        totalRecords,
      })
    );

    dispatch(
      optimisticCategoryMove({
        documentId: docId,
        category: selectedValue,
        document: updateMeta || fileProp,
        currentFolderId,
      })
    );

    return dispatch(
      updateCategory({
        id: docId,
        category: selectedValue,
      })
    )
      .unwrap()
      .then((updatedDocument) => {
        dispatch(
          reconcileCategoryMove({
            documentId: docId,
            category: updatedDocument?.doc_category || selectedValue,
            document: updatedDocument?.document || updatedDocument,
            destinationFolder: updatedDocument?.destination_folder,
            currentFolderId,
          })
        );
        dispatch(getDocumentDetails(docId));
        dispatch(fetchMatterDetail(matterId));
        dispatch(
          fetchMatterDetailList({
            id: matterId,
            page: currentPage,
            sort_direction: sort,
            sort_by: sortBy,
            ...(currentFolderId && currentFolderId !== "root"
              ? { folder: `F${currentFolderId}` }
              : {}),
          })
        );
        const res = {
          id: Date.now().toString(),
          newMsg: "Category added successfully",
          success: true,
        };
        setError([res]); //success message
        setShowNotification(true);
      })
      .catch((err) => {
        dispatch(restoreCategoryMoveSnapshot(snapshot));
        const id = Date.now().toString();
        const newMsg =
          err?.response?.data?.error || err?.response?.data?.detail;
        setError([{ id, newMsg }]);
        setShowNotification(true);
      });
  };

  useEffect(() => {
    dispatch(setUpdateMeta(fileProp));
  }, [updateMeta?.unique_id, fileProp, dispatch]);

  useEffect(() => {
    const docId = fileProp?.unique_id?.substring(1);
    if (docId && open) {
      dispatch(getDocumentDetails(docId));
      dispatch(getShareRecipients({ docId, reviewersOnly: true }))
        .then((response) => {
          if (response.payload?.status === 200) {
            setReviewers(response?.payload?.data?.existing_shares);
          }
        })
        .catch((err) => {
          console.error("Error in fetching share modal recipients", err);
        });
    }
    return () => {
      setReviewers([]);
      setRemindStatus(null);
    };
  }, [fileProp?.unique_id, open, setReviewers, setRemindStatus ]);

  return (
    <DrawerWrapper>
      <GlobalDrawerStyles/>
      <Drawer
        placement="right"
        closable={false}
        onClose={onClose}
        open={open}
        key="right"
        style={{ top: "50px !important" }}
        width={420}
        rootClassName="custom-drawer-lower-component"
      >
        <DrawerContent>
          <DrawerlabelContainer>
            <DrawerHeaderLabel>Case File - Details</DrawerHeaderLabel>
            <BackButton>
              <X size={20} onClick={onClose} />
            </BackButton>
          </DrawerlabelContainer>

          <StyledTabs
            defaultActiveKey="1"
            destroyInactiveTabPane={false}
            items={[
              {
                key: "1",
                label: "File properties",
                children: (
                  <FilePropertiesTab
                    updateMeta={updateMeta}
                    handleCaseTypeSelect={handleCaseTypeSelect}
                    categoryList={categoryList}
                    disabled={dropdownDisabled}
                  />
                ),
              },
              {
                key: "2",
                label: "People with Access",
                children: (
                  <PeopleWithAccessTab
                    reviewers={reviewers}
                    remindInProgressIds={remindInProgressIds}
                    handleRemindClick={handleRemindClick}
                    formatSharedDate={formatSharedDate}
                    remindStatus={remindStatus}
                    setShowShareModal={setShowShareModal}
                  />
                ),
              },
            ]}
          />
        </DrawerContent>
      </Drawer>
    </DrawerWrapper>
  );
};

const ReviewerPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  status: PropTypes.string.isRequired,
  shared_at: PropTypes.string,
  can_remind: PropTypes.bool,
  shared_with: PropTypes.shape({
    full_name: PropTypes.string,
    username: PropTypes.string,
  }),
});

DrawerComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  fileProp: PropTypes.object.isRequired,
  currentPage: PropTypes.number.isRequired,
  sort: PropTypes.string.isRequired,
  sortBy: PropTypes.string.isRequired,
  setError: PropTypes.func.isRequired,
  setShowNotification: PropTypes.func.isRequired,
  setShowShareModal: PropTypes.func.isRequired,
  reviewers: PropTypes.arrayOf(ReviewerPropType).isRequired,
  setReviewers: PropTypes.func.isRequired,
  handleRemindClick: PropTypes.func.isRequired,
  remindStatus: PropTypes.shape({
    user: PropTypes.string,
    success: PropTypes.bool,
  }),
  setRemindStatus: PropTypes.func.isRequired,
  remindInProgressIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default DrawerComponent;
